import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { BadgeEuro, Loader2, Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const FINAL_STATES = ['EXPIRED', 'DECLINED', 'ACCEPTED', 'MISSED'];

const DECLINE_REASONS = [
  'FARE_TOO_LOW', 'NO_VEHICLE_AVAILABILITY', 'NO_DRIVER_AVAILABILITY',
  'ADDONS_NOT_AVAILABLE', 'HOLIDAY', 'STRIKE', 'DISTANCE_TOO_SHORT_OR_LONG', 'OTHER',
];

const statusStyle = (status) => {
  switch (status) {
    case 'ACCEPTED': return 'bg-emerald-100 text-emerald-700';
    case 'DECLINED': return 'bg-red-100 text-red-700';
    case 'EXPIRED': return 'bg-slate-200 text-slate-500';
    case 'MISSED': return 'bg-orange-100 text-orange-700';
    default: return 'bg-sky-100 text-sky-700';
  }
};

// expiry arrives as UTC (docs say CET but real data is UTC)
const expiryAsUtcIso = (raw) => (/Z$|[+-]\d{2}:?\d{2}$/.test(raw || '') ? raw : `${raw}Z`);

const expiryAthens = (raw) => {
  if (!raw) return null;
  try {
    return new Date(expiryAsUtcIso(raw)).toLocaleString('it-IT', {
      timeZone: 'Europe/Athens',
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return raw;
  }
};

const isExpired = (raw) => {
  if (!raw) return true;
  try { return new Date(expiryAsUtcIso(raw)).getTime() <= Date.now(); } catch { return true; }
};

// pickUpTime.localTime is already the journey's local time: format WITHOUT timezone conversion
const formatPickupTime = (localTime) => {
  if (!localTime) return '—';
  const m = String(localTime).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}, ${m[4]}:${m[5]}`;
  return localTime;
};

const OfferSummary = ({ offer }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm space-y-1" data-testid="offer-confirm-summary">
    <div><span className="text-slate-500">ID offerta:</span> <span className="font-mono font-medium">{offer.offer_id}</span></div>
    <div><span className="text-slate-500">Codice viaggio:</span> <span className="font-mono">{offer.journey_code || '—'}</span></div>
    <div><span className="text-slate-500">Ritiro:</span> {offer.pickup || '—'}</div>
    <div><span className="text-slate-500">Destinazione:</span> {offer.dropoff || '—'}</div>
    <div><span className="text-slate-500">Data ritiro:</span> {formatPickupTime(offer.pickup_time)}</div>
    <div><span className="text-slate-500">Veicolo:</span> {offer.vehicle || '—'}</div>
    <div><span className="text-slate-500">Tariffa:</span> {offer.fare?.includingVat != null ? `€${Number(offer.fare.includingVat).toFixed(2)}` : '—'}</div>
  </div>
);

export default function TransferzOffers() {
  const { token } = useAuth();
  const [offers, setOffers] = useState([]);
  const [liveSync, setLiveSync] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [action, setAction] = useState(null); // { type: 'accept' | 'decline', offer }
  const [meetingPointId, setMeetingPointId] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [declineDescription, setDeclineDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchOffers = useCallback(() => {
    return axios.get(`${API}/transferz/offers`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        setOffers(r.data.offers || []);
        setLiveSync(r.data.live_sync !== false);
      })
      .catch(() => setError('Impossibile caricare le offerte Transferz'));
  }, [token]);

  useEffect(() => {
    fetchOffers().finally(() => setLoading(false));
  }, [fetchOffers]);

  const canAct = (o) =>
    o.status === 'NOTIFIED' && !o.needs_verification && !isExpired(o.expiry);

  const openAction = (type, offer) => {
    setActionError(null);
    setSubmitting(false);
    setDeclineReason('');
    setDeclineDescription('');
    const points = offer.meeting_points || [];
    setMeetingPointId(points.length === 1 ? (points[0].id ?? 0) : null);
    setAction({ type, offer });
  };

  const closeAction = () => {
    if (!submitting) setAction(null);
  };

  const confirmAction = async () => {
    if (!action || submitting) return;
    const { type, offer } = action;
    setSubmitting(true);
    setActionError(null);
    try {
      if (type === 'accept') {
        let body = null;
        if (offer.meeting_point_required) {
          const mp = (offer.meeting_points || []).find(p => (p.id ?? 0) === meetingPointId);
          body = { meetingPointId: mp?.id ?? meetingPointId, type: mp?.type };
        }
        await axios.post(`${API}/transferz/offers/${offer.offer_id}/accept`, body, authHeaders);
        toast.success(`Offerta ${offer.offer_id} accettata`);
      } else {
        await axios.post(`${API}/transferz/offers/${offer.offer_id}/decline`, {
          reason: declineReason,
          description: declineDescription,
        }, authHeaders);
        toast.success(`Offerta ${offer.offer_id} rifiutata`);
      }
      setAction(null);
      await fetchOffers();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Errore imprevisto. Riprova.';
      setActionError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setSubmitting(false);
    }
  };

  const acceptDisabled = action?.type === 'accept'
    && action.offer.meeting_point_required
    && (action.offer.meeting_points || []).length > 0
    && meetingPointId === null;

  const declineDisabled = action?.type === 'decline'
    && (!declineReason || (declineReason === 'OTHER' && !declineDescription.trim()));

  return (
    <div className="p-4 lg:p-8" data-testid="transferz-offers-panel">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
          <BadgeEuro className="h-5 w-5 text-amber-600" />
        </div>
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-900">Offerte Transferz</h1>
          <p className="text-sm text-slate-500">Offerte ricevute via webhook — accesso riservato agli admin</p>
        </div>
      </div>

      {!loading && !error && !liveSync && (
        <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3 text-sm" data-testid="transferz-offers-stale-warning">
          ⚠ Stato non aggiornato — impossibile contattare Transferz. Sono mostrati gli stati noti dai webhook.
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento...
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4" data-testid="transferz-offers-error">{error}</div>
      ) : offers.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500" data-testid="transferz-offers-empty">
          Nessuna offerta ricevuta finora.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full" data-testid="transferz-offers-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">ID Offerta</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">ID Viaggio</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Codice</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Stato</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Scadenza (Atene)</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Ritiro</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Destinazione</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Data ritiro</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Veicolo</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tariffa</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {offers.map((o) => {
                const isFinal = FINAL_STATES.includes(o.status);
                const actionable = canAct(o);
                return (
                  <tr key={o.offer_id} className={`border-b border-slate-100 ${isFinal ? 'opacity-60' : 'hover:bg-slate-50'}`} data-testid={`transferz-offer-row-${o.offer_id}`}>
                    <td className="px-4 py-3 text-sm font-mono font-medium text-slate-800">{o.offer_id}</td>
                    <td className="px-4 py-3 text-sm font-mono text-slate-600">{o.journey_id ?? '—'}</td>
                    <td className="px-4 py-3 text-sm font-mono text-slate-600">{o.journey_code || '—'}</td>
                    <td className="px-4 py-3">
                      {o.needs_verification ? (
                        <span className="inline-flex flex-col items-start" data-testid={`offer-status-${o.offer_id}`}>
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">Da verificare</span>
                          <span className="text-[10px] text-slate-400 mt-0.5">ultimo noto: {o.status || '—'}</span>
                        </span>
                      ) : (
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${statusStyle(o.status)}`} data-testid={`offer-status-${o.offer_id}`}>
                          {o.status || '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                      {expiryAthens(o.expiry) || '—'}
                      {o.expiry && <div className="text-[10px] text-slate-400">UTC: {o.expiry}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 max-w-[200px] truncate" title={o.pickup || ''}>{o.pickup || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 max-w-[200px] truncate" title={o.dropoff || ''}>{o.dropoff || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{formatPickupTime(o.pickup_time)}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{o.vehicle || '—'}</td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-800 whitespace-nowrap">
                      {o.fare?.includingVat != null ? `€${Number(o.fare.includingVat).toFixed(2)}` : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          disabled={!actionable}
                          onClick={() => openAction('accept', o)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 disabled:opacity-40"
                          data-testid={`accept-offer-${o.offer_id}`}
                        >
                          <Check className="h-4 w-4 mr-1" /> Accetta
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!actionable}
                          onClick={() => openAction('decline', o)}
                          className="border-red-300 text-red-600 hover:bg-red-50 h-8 px-3 disabled:opacity-40"
                          data-testid={`decline-offer-${o.offer_id}`}
                        >
                          <X className="h-4 w-4 mr-1" /> Rifiuta
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirm dialog */}
      <Dialog open={!!action} onOpenChange={(open) => !open && closeAction()}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="offer-action-dialog">
          {action && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {action.type === 'accept' ? 'Accetta offerta' : 'Rifiuta offerta'} {action.offer.offer_id}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <OfferSummary offer={action.offer} />

                {action.type === 'accept' && action.offer.meeting_point_required && (
                  <div>
                    <div className="text-sm font-medium text-slate-700 mb-2">Punto d&apos;incontro (obbligatorio)</div>
                    {(action.offer.meeting_points || []).length === 0 ? (
                      <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        Nessun punto d&apos;incontro disponibile nei dati dell&apos;offerta.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(action.offer.meeting_points || []).map((mp, idx) => (
                          <label key={mp.id ?? idx} className={`flex items-start gap-2 border rounded-lg p-3 cursor-pointer text-sm ${(mp.id ?? 0) === meetingPointId ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200'}`} data-testid={`meeting-point-${mp.id ?? idx}`}>
                            <input
                              type="radio"
                              name="meetingPoint"
                              className="mt-1"
                              checked={(mp.id ?? 0) === meetingPointId}
                              onChange={() => setMeetingPointId(mp.id ?? 0)}
                            />
                            <span>
                              <span className="font-medium">{mp.title || mp.type || `Punto ${idx + 1}`}</span>
                              {mp.type && <span className="ml-2 text-xs text-slate-400">{mp.type}</span>}
                              {mp.description && <div className="text-xs text-slate-500 mt-1">{mp.description}</div>}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {action.type === 'decline' && (
                  <div className="space-y-3">
                    <div>
                      <div className="text-sm font-medium text-slate-700 mb-1">Motivo (obbligatorio)</div>
                      <select
                        value={declineReason}
                        onChange={(e) => setDeclineReason(e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                        data-testid="decline-reason-select"
                      >
                        <option value="">— Seleziona motivo —</option>
                        {DECLINE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-700 mb-1">
                        Descrizione {declineReason === 'OTHER' ? '(obbligatoria)' : '(facoltativa)'}
                      </div>
                      <textarea
                        value={declineDescription}
                        onChange={(e) => setDeclineDescription(e.target.value)}
                        rows={2}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                        data-testid="decline-description-input"
                      />
                    </div>
                  </div>
                )}

                {actionError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" data-testid="offer-action-error">
                    {actionError}
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={closeAction} disabled={submitting} data-testid="offer-action-cancel">
                    Annulla
                  </Button>
                  <Button
                    onClick={confirmAction}
                    disabled={submitting || acceptDisabled || declineDisabled}
                    className={action.type === 'accept' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
                    data-testid="offer-action-confirm"
                  >
                    {submitting ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Invio...</>) : (action.type === 'accept' ? 'Conferma accettazione' : 'Conferma rifiuto')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
